import { PaginationBar } from '@components/PaginationBar';
import JobApplicationKanban from '@modules/jobApplications/components/JobApplicationKanban';
import {
    defaultJobApplicationFilters,
    IJobApplicationDetailFilter,
    JobApplicationDetailFilter,
} from '@modules/jobApplications/JobApplicationFilter';
import JobApplicationGrid from '@modules/jobApplications/JobApplicationGrid';
import JobApplicationDataTable from '@modules/jobApplications/JobApplicationDataTable';
import { JobApplicationStatus } from '@modules/jobApplications/statuses';
import { JobApplicationDisplayMode, JobApplicationRaw, KanbanData } from '@modules/jobApplications/types';
import { JobApplicationsOperationsProvider } from '@modules/jobApplications/context/JobApplicationsOperationsContext';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useMemo, useState } from 'react';

const FilterContainer = styled(Box)(({ theme }) => ({
    position: 'sticky',
    top: '118px',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    rowGap: '24px',
    background: theme.palette.neutralBlack.main,
}));

const ContentContainer = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    height: '100%',
    background: theme.palette.neutralBlack.main,
}));

/**
 * Get header columns based on display mode
 */
const getHeaderColumnList = (displayMode: JobApplicationDisplayMode) => {
    switch (displayMode) {
        case 'job-focus':
            // When viewing a talent's applications, show job details
            return [
                { id: 0, title: "Offre d'emploi", minWidth: '240px' },
                { id: 1, title: 'Marque', minWidth: '220px' },
                { id: 2, title: 'Exp', minWidth: '80px' },
                { id: 3, title: 'Diplômes', minWidth: '200px' },
                { id: 4, title: 'Ville', minWidth: '100px' },
                { id: 5, title: 'Salaire moyen', minWidth: '190px' },
                { id: 6, title: 'Date de candidature', minWidth: '130px' },
                { id: 7, title: '', minWidth: '180px' },
            ];
        case 'talent-focus':
            // When viewing job applications, show talent details
            return [
                { id: 0, title: 'Candidat', minWidth: '240px' },
                { id: 1, title: 'Notation', minWidth: '120px' },
                { id: 2, title: 'Ville', minWidth: '100px' },
                { id: 3, title: 'Exp', minWidth: '80px' },
                { id: 4, title: 'Contrat', minWidth: '100px' },
                { id: 5, title: 'Diplômes', minWidth: '200px' },
                { id: 6, title: 'Source', minWidth: '100px' },
                { id: 7, title: 'Date de candidature', minWidth: '130px' },
                { id: 8, title: '', minWidth: '180px' },
            ];
        case 'full':
        default:
            // Show both talent and job info
            return [
                { id: 0, title: 'Candidat', minWidth: '240px' },
                { id: 1, title: 'Marque', minWidth: '220px' },
                { id: 2, title: 'Exp', minWidth: '80px' },
                { id: 3, title: 'Diplômes', minWidth: '200px' },
                { id: 4, title: 'Ville', minWidth: '100px' },
                { id: 5, title: 'Salaire moyen', minWidth: '190px' },
                { id: 6, title: 'Date de candidature', minWidth: '130px' },
                { id: 7, title: '', minWidth: '180px' },
            ];
    }
};

export interface JobApplicationListContainerProps {
    /**
     * Job applications data to display (raw backend data)
     * Used for list and grid view modes
     */
    data: JobApplicationRaw[];
    /**
     * Kanban organized data with pagination per status
     * Used for kanban view mode with infinite scroll
     */
    kanbanData?: KanbanData;
    /**
     * Loading state
     */
    loading?: boolean;
    /**
     * Total number of documents
     */
    totalDocs?: number;
    /**
     * Total number of pages
     */
    totalPages?: number;
    /**
     * Backend pagination metadata
     */
    meta?: {
        totalDocs?: number;
        page?: number;
        limit?: number;
        totalPages?: number;
    };
    /**
     * Current page
     */
    currentPage?: number;
    /**
     * Items per page
     */
    itemsPerPage?: number;
    /**
     * Callback when page changes
     */
    onPageChange?: (page: number) => void;
    /**
     * Callback when items per page changes
     */
    onItemsPerPageChange?: (limit: number) => void;
    /**
     * Callback when search query changes
     */
    onQueryChange?: (query: string) => void;
    /**
     * Callback when sort changes
     */
    onSortChange?: (sortBy: { field: string; order: number }) => void;
    /**
     * Callback when filter changes
     */
    onFilterChange?: (filter: IJobApplicationDetailFilter) => void;
    /**
     * Current filter state
     */
    filter?: IJobApplicationDetailFilter;
    /**
     * Show filter controls (default: true)
     */
    showFilter?: boolean;
    /**
     * Show pagination (default: true)
     */
    showPagination?: boolean;
    /**
     * Initial view mode (default: 'list')
     */
    defaultViewMode?: 'grid' | 'list' | 'kanban';
    /**
     * Display mode for job applications
     * - 'full': Show both talent and job offer details (default)
     * - 'job-focus': Emphasize job offer details (for talent detail page)
     * - 'talent-focus': Emphasize talent details (for job offer detail page)
     */
    displayMode?: JobApplicationDisplayMode;
    /**
     * Loading more rows via scroll chunks
     */
    isLoadingMore?: boolean;
    /**
     * Trigger next chunk fetch on scroll
     */
    onLoadMore?: () => void;
    /**
     * Callback for kanban infinite scroll - load more items for a specific status
     */
    onLoadMoreKanban?: (status: string) => void;
    /**
     * Callback when job application status is updated via kanban drag and drop
     */
    onStatusUpdate?: (applicationId: number, newStatus: JobApplicationStatus) => void;
    /**
     * Chips to be hidden
     */
    hideChipKeys?: string[];
    /**
     * Callback to force-clear the backend cache and re-fetch fresh data
     */
    onRefresh?: (proId?: string) => void | Promise<void>;
    /**
     * Initial search query to pre-fill from URL params
     */
    initialQuery?: string;
}

/**
 * Reusable Job Application List Container
 *
 * This component provides a complete job application list interface with:
 * - Filter and search capabilities
 * - Grid/List/Kanban view mode toggle
 * - Pagination (for grid and list modes)
 * - Infinite scroll (for kanban mode)
 * - Customizable data source
 *
 * It can be used for:
 * - Job-specific applications (with job offer filter)
 * - All applications across the system
 * - Talent-specific applications
 *
 * For kanban mode, use kanbanData prop instead of data for better performance
 * with per-status pagination and infinite scroll.
 */
const JobApplicationListContainer: React.FC<JobApplicationListContainerProps> = ({
    data,
    kanbanData,
    loading = false,
    isLoadingMore,
    onLoadMore,
    totalDocs = 0,
    totalPages = 0,
    meta,
    currentPage = 1,
    itemsPerPage = 10,
    onPageChange,
    onItemsPerPageChange,
    onQueryChange,
    onSortChange,
    onFilterChange,
    filter = defaultJobApplicationFilters,
    showFilter = true,
    showPagination = true,
    defaultViewMode = 'list',
    displayMode = 'talent-focus',
    onLoadMoreKanban,
    onStatusUpdate,
    hideChipKeys = [],
    onRefresh,
    initialQuery,
}) => {
    const [viewMode, setViewMode] = useState<'grid' | 'list' | 'kanban'>(defaultViewMode);

    const changeViewMode = (event: React.MouseEvent<HTMLButtonElement>) => {
        setViewMode(event.currentTarget.value as 'grid' | 'list' | 'kanban');
    };

    // Get header columns based on display mode
    const headerColumnList = useMemo(() => getHeaderColumnList(displayMode), [displayMode]);

    const handleQueryChange = useCallback(
        (value: string) => {
            if (onQueryChange) {
                onQueryChange(value);
            }
        },
        [onQueryChange],
    );

    const handleSortChange = useCallback(
        (newSortBy: { field: string; order: number }) => {
            if (onSortChange) {
                onSortChange(newSortBy);
            }
        },
        [onSortChange],
    );

    const handleFilterChange = useCallback(
        (newFilter: IJobApplicationDetailFilter) => {
            if (onFilterChange) {
                onFilterChange(newFilter);
            }
        },
        [onFilterChange],
    );

    const handlePageChange = useCallback(
        (page: number) => {
            if (onPageChange) {
                onPageChange(page);
            }
        },
        [onPageChange],
    );

    const handleItemsPerPageChange = useCallback(
        (limit: number) => {
            if (onItemsPerPageChange) {
                onItemsPerPageChange(limit);
            }
        },
        [onItemsPerPageChange],
    );

    const handleConfirm = (params: {
        title: string;
        description: string;
        confirmTitle: string;
        cancelTitle: string;
        action: string;
    }) => {
        // Handle confirmation dialog
        console.log('Confirm action:', params);
    };

    return (
        <JobApplicationsOperationsProvider>
            {showFilter && (
                <FilterContainer>
                    <JobApplicationDetailFilter
                        viewMode={viewMode}
                        changeViewMode={changeViewMode}
                        totalDocs={totalDocs}
                        onSortChange={handleSortChange}
                        onQueryChange={handleQueryChange}
                        onFilterChange={handleFilterChange}
                        filter={filter}
                        hideChipKeys={hideChipKeys}
                        onRefresh={onRefresh}
                        initialQuery={initialQuery}
                    />
                </FilterContainer>
            )}

            <ContentContainer>
                {/* {viewMode === 'kanban' && (
                    <JobApplicationKanban
                        kanbanData={kanbanData}
                        data={!kanbanData ? data : undefined}
                        displayMode={displayMode}
                        onLoadMore={onLoadMoreKanban}
                        onStatusUpdate={onStatusUpdate}
                    />
                )} */}
                {viewMode === 'list' && (
                    <JobApplicationDataTable
                        headerColumnList={headerColumnList}
                        data={data}
                        meta={meta}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        onSortChange={handleSortChange}
                        onConfirm={handleConfirm}
                        loading={loading}
                        isLoadingMore={isLoadingMore}
                        onScrollEnd={onLoadMore}
                        displayMode={displayMode}
                    />
                )}

                {/* {viewMode === 'grid' && <JobApplicationGrid data={data} loading={loading} displayMode={displayMode} />} */}
            </ContentContainer>

            {showPagination && viewMode === 'grid' && (
                <PaginationBar
                    totalPages={totalPages}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    setCurrentPage={handlePageChange}
                    setItemsPerPage={handleItemsPerPageChange}
                />
            )}
        </JobApplicationsOperationsProvider>
    );
};

export default JobApplicationListContainer;
